// Trust badges + "Why this source?" explanations - the user-facing face of the
// connection layer. Two hard rules enforced here:
//   1. AI-suggested NEVER looks like verified. The AI/review checks run FIRST,
//      before any evidence-based upgrade can fire.
//   2. An edge with no evidence renders as a warning, never as verified.
// Pure projections over SourceEdge + Evidence - no DOM, no fetch, no guessing.

import { validateSourceEdge } from "./sourceValidation.ts";
import type { Evidence, Passage, SourceEdge } from "./sourceGraph.ts";

export type TrustTone = "verified" | "probable" | "suggested" | "warning" | "neutral";

export interface TrustBadge {
  label: string;
  tone: TrustTone;
  explanation: string;
}

const has = (evidence: Evidence[] | undefined, type: Evidence["evidenceType"]): boolean =>
  !!evidence?.some((e) => e.evidenceType === type);

export function getTrustBadge(edge: SourceEdge, evidence?: Evidence[]): TrustBadge {
  // --- order matters: review flags and AI/suggested checks come FIRST -------
  if (edge.reviewed?.status === "needs_correction" || edge.reviewed?.status === "rejected") {
    return {
      label: "Needs review",
      tone: "warning",
      explanation: "A reviewer flagged this connection as needing correction.",
    };
  }
  if (edge.confidence === "rejected") {
    return { label: "Rejected", tone: "warning", explanation: "This connection was reviewed and rejected." };
  }
  if (edge.provenance.origin === "ai_suggested" || edge.edgeClass === "ai") {
    return {
      label: "AI-suggested",
      tone: "suggested",
      explanation: "Suggested by AI. Not a verified citation - treat as a lead, not a proof.",
    };
  }
  if (edge.confidence === "suggested" || edge.edgeClass === "topic") {
    return {
      label: "Suggested",
      tone: "suggested",
      explanation: "A thematic suggestion, not a verified citation.",
    };
  }

  // --- no evidence / failed validation can never be a normal link -----------
  const v = validateSourceEdge(edge);
  if (!v.valid) {
    return { label: "Unverified", tone: "warning", explanation: `Incomplete connection: ${v.errors[0]}` };
  }
  if (edge.evidenceIds.length === 0 && (!evidence || evidence.length === 0)) {
    return {
      label: "Unverified",
      tone: "warning",
      explanation: "No evidence is recorded for this connection.",
    };
  }
  // Dangling evidence: the edge CLAIMS evidence ids but the caller resolved
  // them to nothing. That must degrade exactly like no evidence - an
  // unresolvable claim can never carry a verified badge.
  if (evidence && evidence.length === 0 && edge.evidenceIds.length > 0) {
    return {
      label: "Unverified",
      tone: "warning",
      explanation: "This connection's recorded evidence could not be found.",
    };
  }

  // --- evidence-based upgrades (only reachable for non-AI, evidenced edges) --
  if (edge.edgeClass === "structural" || has(evidence, "structural_commentary_link")) {
    return {
      label: "Commentary structure",
      tone: "verified",
      explanation: "This work is structurally a commentary on the connected passage.",
    };
  }
  if (has(evidence, "quoted_text") || has(evidence, "phrase_match")) {
    return {
      label: "Quote match",
      tone: edge.confidence === "verified" ? "verified" : "probable",
      explanation: "The connected passage's words appear in this text.",
    };
  }
  if (edge.confidence === "verified" && has(evidence, "explicit_ref")) {
    return {
      label: "Verified citation",
      tone: "verified",
      explanation: "The text explicitly cites the connected passage.",
    };
  }
  if (has(evidence, "explicit_ref")) {
    return {
      label: "Cited in text",
      tone: "probable",
      explanation: "The text names the connected passage's title and location.",
    };
  }
  if (has(evidence, "manual_editorial_link")) {
    return {
      label: "Editor-verified",
      tone: edge.confidence === "verified" ? "verified" : "probable",
      explanation: "An editor recorded this connection manually.",
    };
  }
  if (has(evidence, "provider_link")) {
    const provider = evidence?.find((e) => e.evidenceType === "provider_link")?.provider ?? "the provider";
    return {
      label: "Provider link",
      tone: edge.confidence === "verified" ? "verified" : "probable",
      explanation: `Recorded as a connection by ${provider}.`,
    };
  }

  // --- fallbacks by bare confidence ------------------------------------------
  if (edge.confidence === "verified") {
    return { label: "Verified", tone: "verified", explanation: "A verified connection." };
  }
  if (edge.confidence === "high") {
    return { label: "Probable", tone: "probable", explanation: "A probable connection." };
  }
  return { label: "Unverified", tone: "neutral", explanation: "Confidence in this connection is low." };
}

// ---------------------------------------------------------------------------
// "Why this source?" - one plain, honest sentence (or two). Built ONLY from
// the edge's typed relationship + its evidence records; never invents a claim.
// Suggested/AI connections explicitly say they are not verified citations.
// ---------------------------------------------------------------------------

const REL_PHRASE: Partial<Record<SourceEdge["relationshipType"], string>> = {
  comments_on: "it is a commentary on",
  supercomments_on: "it is a supercommentary on",
  cites: "it cites",
  quotes: "it quotes",
  is_quoted_by: "it is quoted by",
  cross_reference: "it is cross-referenced with",
  parallel_source: "it is a parallel source to",
  same_base_text: "it is on the same base text as",
  same_phrase: "it shares a phrase with",
  same_topic: "it shares a topic with",
  translation_of: "it is a translation of",
  explains: "it explains",
};

export function explainSourceConnection(
  edge: SourceEdge,
  evidence: Evidence[],
  from?: Passage,
  to?: Passage,
): string {
  const fromName = from?.displayTitle ?? edge.fromRef;
  const toName = to?.displayTitle ?? edge.toRef;
  // trim a trailing period off the body so an evidence explanation that ends
  // with one never doubles up ("...the spies.." reads sloppy)
  const lead = (body: string): string => `This source appears because ${body.replace(/\.+\s*$/, "")}.`;

  // Prefer the strongest evidence record's own explanation.
  const byStrength: Evidence["evidenceType"][] = [
    "structural_commentary_link",
    "explicit_ref",
    "quoted_text",
    "phrase_match",
    "manual_editorial_link",
    "provider_link",
    "user_annotation",
    "topic_match",
    "ai_explanation",
  ];
  const best = byStrength
    .map((t) => evidence.find((e) => e.evidenceType === t))
    .find((e): e is Evidence => !!e);

  let sentence: string;
  switch (best?.evidenceType) {
    case "structural_commentary_link":
      sentence = lead(`it is structurally a commentary on ${toName}`);
      break;
    case "explicit_ref":
      sentence = lead(`${fromName} explicitly cites ${toName} in its own words`);
      break;
    case "quoted_text":
    case "phrase_match": {
      const quote = best.heQuoteText ?? best.quoteText;
      sentence = quote
        ? lead(`the quoted phrase "${quote}" matches ${toName}`)
        : lead(`its words match ${toName}`);
      break;
    }
    // These two name BOTH endpoints: the claim is symmetric/orientation-free,
    // and the card being explained may be EITHER endpoint - naming only toName
    // can produce a self-referential sentence ("X is connected to X's ref").
    case "manual_editorial_link":
      sentence = lead(`an editor recorded ${fromName} and ${toName} as connected`);
      break;
    case "provider_link":
      sentence = lead(`${best.provider ?? "the text provider"} records ${fromName} and ${toName} as connected`);
      break;
    case "user_annotation":
      sentence = lead(`you connected it to ${toName}`);
      break;
    case "topic_match":
      sentence = lead(
        best.explanation
          ? `both sources share a theme: ${best.explanation}`
          : `both sources share a theme`,
      );
      break;
    case "ai_explanation":
      sentence = lead(best.explanation ? `AI suggested it: ${best.explanation}` : `AI suggested it`);
      break;
    default: {
      // No evidence record at hand: fall back to the typed relationship, and
      // say so honestly if even that is missing.
      const phrase = REL_PHRASE[edge.relationshipType];
      sentence = phrase
        ? lead(`${phrase} ${toName}`)
        : `This connection has no recorded evidence yet - treat it as unverified.`;
    }
  }

  // Suggested/AI must never read like a verified citation.
  const badge = getTrustBadge(edge, evidence);
  if (badge.tone === "suggested") {
    sentence += " This is a suggested connection, not a verified citation.";
  } else if (badge.tone === "warning") {
    sentence += ` (${badge.explanation})`;
  }
  return sentence;
}
